<template>
	<div class="relative w-full min-h-[40px]">
		<div v-if="loading" class="absolute inset-0">
			<div class="w-full h-full border border-ihh-gray/40 rounded flex items-center justify-center bg-white">
				<Spinner :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
			</div>
		</div>
		<div :class="{ invisible: loading }" id="google-pay-button"></div>
	</div>
</template>

<script>
import helpers from '../../helpers/checkout.js'
import braintree from 'braintree-web'
import axios from 'axios'
import { useCheckoutStore } from '../../stores/store.js'
import Swal from 'sweetalert2'

export default {
	name: 'GooglePayButton',
	inject: ['store'],
	data() {
		return {
			checkout: useCheckoutStore(),
			isProcessing: false,
			loading: true,
			paymentsClient: null,
			clientToken: null,
			deviceData: null,
			googlePaymentInstance: null,
			googlePayLoaded: false,
			allowedPaymentMethods: null,
		}
	},

	async mounted() {
		await this.setup()
	},

	beforeUnmount() {
		// Clean up any resources if needed
	},

	methods: {
		async loadScripts() {
			const scripts = [
				'https://pay.google.com/gp/p/js/pay.js',
				'https://js.braintreegateway.com/web/3.88.4/js/client.min.js',
				'https://js.braintreegateway.com/web/3.88.4/js/google-payment.min.js',
			]

			for (const src of scripts) {
				await new Promise((resolve, reject) => {
					const script = document.createElement('script')
					script.src = src
					script.async = true
					script.onload = resolve
					script.onerror = reject
					document.body.appendChild(script)
				})
			}
		},
		async setup() {
			try {
				// Load Google Pay and Braintree scripts (if not already loaded)
				console.log('Loading scripts...')
				await this.loadScripts()

				// Initialize Google Pay PaymentsClient
				console.log('Creating Payments Client...')
				const paymentsClient = new google.payments.api.PaymentsClient({
					environment: 'PRODUCTION', // 'TEST' or 'PRODUCTION'
				})

				// Fetch Braintree client token
				console.log('Fetching Braintree client token...')
				const { success, token } = await helpers.getClientToken() // change to getSandboxClientToken() for testing
				if (!success) {
					console.error('Failed to get Braintree client token')
					this.$emit('updateAvailability', false)
					return { result: false }
				}

				// Initialize Braintree client
				console.log('Initializing Braintree client...')
				const clientInstance = await braintree.client.create({
					authorization: token,
				})

				// Fetch device data
				console.log('Fetching device data...')
				const dataCollectorInstance = await braintree.dataCollector.create({ client: clientInstance })
				this.deviceData = dataCollectorInstance.deviceData

				// Initialize Braintree Google Pay component
				console.log('Initializing Braintree Google Pay component...')
				const googlePaymentInstance = await braintree.googlePayment.create({
					client: clientInstance,
					googlePayVersion: 2,
					googleMerchantId: 'BCR2DN4TYPLMBMZO', // Required in PRODUCTION; must be a valid ID even in TEST
				})

				// Check if Google Pay is available
				console.log('Checking Google Pay availability...')
				const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest()
				const isReadyResponse = await paymentsClient.isReadyToPay({
					apiVersion: 2,
					apiVersionMinor: 0,
					allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods,
					// existingPaymentMethodRequired: true, // Optional: requires user to have a saved payment method
				})

				if (!isReadyResponse.result) {
					console.log('Google Pay is not available')
					this.$emit('updateAvailability', false)
					return { result: false }
				}

				// Create and append Google Pay button
				console.log('Creating Google Pay button...')
				const buttonContainer = document.getElementById('google-pay-button')
				const button = paymentsClient.createButton({
					buttonColor: 'default', // Options: 'default', 'black', 'white'
					buttonType: 'buy', // Options: 'buy', 'plain', etc.
					buttonRadius: 4,
					buttonSizeMode: 'fill',
					onClick: async () => {
						try {
							if (this.isProcessing) {
								console.log('Payment processing already in progress, ignoring click')
								return
							}
							this.isProcessing = true

							// Create payment data request
							const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest({
								transactionInfo: {
									currencyCode: 'USD',
									totalPriceStatus: 'FINAL',
									totalPrice: this.checkout.remainingBalance.toString(),
								},
							})

							// Add billing address requirements
							const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0]
							cardPaymentMethod.parameters.billingAddressRequired = true
							cardPaymentMethod.parameters.billingAddressParameters = {
								format: 'FULL',
								phoneNumberRequired: true,
							}

							// Load payment data from Google Pay
							console.log('Loading payment data...')
							const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest)

							// Process payment with Braintree
							console.log('Processing payment with Braintree...')
							const result = await googlePaymentInstance.parseResponse(paymentData)
							console.log('Payment nonce:', result.nonce, 'Type:', result.type)
							console.log('Process payment result: ', JSON.stringify(result))

							// Log success event with nonce (send to server for transaction processing)
							console.log('paymentProcessed', {
								success: true,
								nonce: result.nonce,
								type: result.type, // 'AndroidPayCard' or 'PayPalAccount'
								billingAddress: paymentData.paymentMethodData.info.billingAddress,
							})

							this.checkout.updateShowOverlay(true)
							await this.completeOrder(result.nonce)
						} catch (error) {
							this.isProcessing = false
							this.checkout.updateShowOverlay(false)
							console.error('Error processing Google Pay:', error)
							console.log('paymentProcessed', { success: false })
						}
					},
					allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods, // Use Braintree-derived payment methods
				})

				buttonContainer.appendChild(button)
				this.loading = false
				this.$emit('updateAvailability', true)
				return { result: true }
			} catch (error) {
				this.isProcessing = false
				console.error('Error setting up Google Pay:', error)
				this.$emit('updateAvailability', false)
				return { result: false }
			}
		},
		async completeOrder(nonce) {
			try {
				// Create order in BigCommerce
				const bcOrderResponse = await helpers.placeOrder({ paymentMethod: 'apm' })

				const bcOrderId = bcOrderResponse?.bcOrderId
				if (!bcOrderId) {
					this.isProcessing = false
					throw new Error('Failed to create BigCommerce order.')
				}

				// Process payment with Braintree
				const paymentResponse = await helpers.processBraintreePayment({
					nonce,
					amount: this.grandTotal,
					bcOrderId,
					deviceData: this.deviceData,
				})

				if (!paymentResponse?.success) {
					this.isProcessing = false
					throw new Error(paymentResponse?.message || 'Payment processing failed.')
				}

				console.log('Payment successful:', paymentResponse.data)
				const { redirectUrl } = await helpers.handleCheckoutSuccess(bcOrderId, paymentResponse, ['submitted_for_settlement', 'settling', 'settled'])

				this.$router.push(redirectUrl)
			} catch (error) {
				this.isProcessing = false
				this.checkout.updateShowOverlay(false)
				console.error('Error completing Google Pay order:', error)
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'There was an error completing your order. Please try again.',
					confirmButtonText: 'OK',
				})
			}
		},
	},

	computed: {
		subTotal() {
			return Number(parseFloat(this.store?.cart?.total).toFixed(2))
		},
		subTotalWithDiscounts() {
			// coupon discounts are already applied to the cart subTotal
			const total = this.subTotal - this.checkout.giftCertificateAmount - this.checkout.storeCredit
			return Number(parseFloat(total).toFixed(2))
		},
		grandTotal() {
			let total = this.subTotalWithDiscounts + (this.checkout.shipping.selectedShipping?.cost || 0)
			if (total <= 0) total = this.subTotal
			return Number(parseFloat(total).toFixed(2))
		},
	},
}
</script>

<style scoped>
#google-pay-button {
	width: 100%;
	height: 40px;
}

#google-pay-button > div {
	width: 100% !important;
	height: 40px !important;
}

#google-pay-button iframe {
	height: 40px !important;
}
</style>
